import React from 'react';
import AnalysisMarkdown from '../common/AnalysisMarkdown';
import QuestionRenderer from './QuestionRenderer';
import { SurveyResults, LegacyTableData } from './types';

interface ProfessionalSurveyResultsProps {
  data: SurveyResults | { tables: LegacyTableData[] };
  contentType?: string;
  isExporting?: boolean;
  className?: string;
  images?: string[];
  onImageClick?: (src: string, alt: string) => void;
}

const ProfessionalSurveyResults: React.FC<ProfessionalSurveyResultsProps> = ({ 
  data, 
  contentType, 
  isExporting = false,
  className = '',
  images = [],
  onImageClick
}) => {
  // Check if this is the new enhanced survey format
  const isEnhancedFormat = 'survey_results' in data;
  
  if (!isEnhancedFormat) {
    // Fallback to legacy table rendering for backward compatibility
    const legacyData = data as { tables: LegacyTableData[] };
    return (
      <div className={`space-y-6 ${className}`}>
        {legacyData.tables?.map((table, index) => (
          <div key={index} className="bg-white rounded-lg border border-gray-200 p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">{table.title || `Table ${index + 1}`}</h3>
            <div className="text-gray-600 text-sm">Legacy table format</div>
          </div>
        ))}
      </div>
    );
  }

  const surveyData = data as SurveyResults;
  const cpgAnalysis = surveyData.summary?.cpg_analysis;
  const hasCpgSynthesis = !!cpgAnalysis?.survey_synthesis?.markdown;

  return (
    <div className={`space-y-8 ${className}`}>
      {/* CPG Survey Synthesis (only shown when available) */}
      {hasCpgSynthesis && (
        <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Overall Summary</h2>
          <AnalysisMarkdown>{cpgAnalysis!.survey_synthesis.markdown}</AnalysisMarkdown>
        </div>
      )}

      {/* Question by Question Results (grouped by section when present) */}
      <div className="space-y-6">
        {surveyData.survey_results.questions.map((question, index) => {
          const prevSectionId = index > 0
            ? surveyData.survey_results.questions[index - 1].section_id
            : undefined;
          const startsNewSection =
            !!question.section_id &&
            !!question.section_context &&
            question.section_id !== prevSectionId;
          return (
            <React.Fragment key={question.question_id || index}>
              {startsNewSection && (
                <div className="bg-[#E8FCFA] rounded-lg border border-[#07E5D1]/40 p-6 shadow-sm">
                  {question.section_title && (
                    <div className="text-xs font-semibold uppercase tracking-wide text-[#06D4C2] mb-1">
                      {question.section_title}
                    </div>
                  )}
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Stimulus shown to respondents</h3>
                  <AnalysisMarkdown>{question.section_context as string}</AnalysisMarkdown>
                </div>
              )}
              <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm">
                <QuestionRenderer
                  question={question}
                  index={index}
                  isExporting={isExporting}
                  images={images}
                  onImageClick={onImageClick}
                />
              </div>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

export default ProfessionalSurveyResults;